import React, { useState } from 'react';
import Modal from '../common/Modal';
import { XMarkIcon, BeakerIcon } from '@heroicons/react/24/outline';
import { agents } from '../../../lib/ai/agentCaller';


const SOCIAL_NETWORKS = [
  { id: 'facebook', name: 'Facebook' },
  { id: 'linkedin', name: 'LinkedIn' },
  { id: 'x', name: 'X (Twitter)' }
];

export default function SocialSettingsModal({ 
  isOpen, 
  onClose, 
  data, 
  onSave,
  nodeName 
}) {
  const [settings, setSettings] = useState({
    label: data.label || nodeName,
    agentType: data.agentType || 'dave',
    userPrompt: data.userPrompt || '',
    selectedUpstreamNodes: data.selectedUpstreamNodes || [],
    ...data
  });

  const [isEditingTitle, setIsEditingTitle] = useState(false);

  // Add state for delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...settings,
      folder_id: settings.folder_id
    });
    onClose();
  };

  const handleDelete = (e) => {
    e.preventDefault();
    
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      setTimeout(() => setDeleteConfirm(false), 3000);
      return;
    }

    if (typeof data.onDelete === 'function' && data.id) {
      data.onDelete(data.id);
      onClose();
    }
  };

  
  // Get node info based on type
  const nodeInfo = {
    icon: BeakerIcon,
    label: 'Social Node',
    color: 'text-gray-400'
  }
  const IconComponent = nodeInfo.icon;

  // Get all upstream nodes that aren't trigger nodes
  const upstreamNodes = React.useMemo(() => {
    if (!data.edges || !data.nodes) return [];
    
    const visited = new Set();
    const nodes = [];
  
    const findUpstreamNodes = (currentNodeId) => {
      if (visited.has(currentNodeId)) return;
      visited.add(currentNodeId);
  
      const incomingEdges = data.edges.filter(edge => edge.target === currentNodeId);
      
      for (const edge of incomingEdges) {
        const sourceNode = data.nodes.find(node => node.id === edge.source);
        
        if (sourceNode && sourceNode.type !== 'trigger') {
          nodes.push({
            id: sourceNode.id,
            type: sourceNode.type,
            label: sourceNode.data.label || `Unnamed ${sourceNode.type} Node`
          });
        }
        
        findUpstreamNodes(edge.source);
      }
    };
  
    findUpstreamNodes(data.id);
    return nodes;
  }, [data.edges, data.nodes, data.id]);

  const toggleUpstreamNode = (nodeId) => {
    setSettings(prev => ({
      ...prev,
      selectedUpstreamNodes: prev.selectedUpstreamNodes.includes(nodeId)
        ? prev.selectedUpstreamNodes.filter(id => id !== nodeId)
        : [...prev.selectedUpstreamNodes, nodeId]
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className={`
        flex flex-col
        ${upstreamNodes.length === 0 
          ? 'w-[500px]' // Thinner width when no upstream nodes
          : 'w-[1200px]' // Full width when showing upstream nodes
        }
        max-w-[90vw]
        max-h-[90vh]
      `}>
        <div className="flex flex-1 min-h-0">
          {/* Left side - Upstream Nodes - Only show if there are upstream nodes */}
          {upstreamNodes.length > 0 && (
            <div className="w-[40%] bg-dark-300 p-6 border-r border-dark-400 overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium">Include Results From</h3>
              </div>
              <div className="bg-dark-200 rounded-lg border border-dark-400 flex-1 overflow-y-auto px-2">
                <div className="divide-y divide-dark-400">
                  {upstreamNodes.map((node) => (
                    <button
                      key={node.id}
                      onClick={() => toggleUpstreamNode(node.id)}
                      className={`w-full flex items-center p-4 my-2 rounded-lg hover:bg-dark-400 transition-colors ${
                        settings.selectedUpstreamNodes.includes(node.id)
                          ? 'bg-accent-primary/10 border border-accent-primary'
                          : 'bg-dark-300 border border-dark-400'
                      }`}
                    >
                      <div className="flex-1 text-left">
                        <div className="font-medium text-gray-200">{node.label}</div>
                        <div className="text-sm text-gray-400">{node.type}</div>
                      </div>
                      <div className={`w-3 h-3 rounded-full transition-colors ${
                        settings.selectedUpstreamNodes.includes(node.id)
                          ? 'bg-accent-primary'
                          : 'bg-gray-600'
                      }`} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Right side - Settings */}
          <div className={`${upstreamNodes.length === 0 ? 'w-full' : 'flex-1'} p-6 overflow-y-auto`}>
            {/* Title Section */}
            <div className="mb-6 flex items-center justify-between">
              <div className="flex-1">
                {isEditingTitle ? (
                  <input
                    type="text"
                    value={settings.label}
                    onChange={(e) => setSettings(prev => ({ ...prev, label: e.target.value }))}
                    onBlur={() => setIsEditingTitle(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setIsEditingTitle(false);
                      }
                    }}
                    className="text-2xl font-semibold bg-dark-300 px-3 py-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-primary w-full"
                    autoFocus
                  />
                ) : (
                  <h2 
                    className="text-2xl font-semibold cursor-pointer hover:text-accent-primary transition-colors"
                    onClick={() => setIsEditingTitle(true)}
                  >
                    {settings.label}
                  </h2>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-dark-200 rounded-full">
                  <IconComponent className={`w-4 h-4 ${nodeInfo.color}`} />
                  <span className="text-sm text-gray-400">{nodeInfo.label}</span>
                </div>
                <button onClick={handleSubmit} className="text-gray-400 hover:text-gray-300">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {/* Agent Type */}
              <div>
                <label className="block text-sm font-medium mb-2">Social Network</label>
                <select
                  value={settings.network}
                  onChange={(e) => setSettings(prev => ({ ...prev, network: e.target.value }))}
                  className="w-full px-3 py-2 bg-dark-200 border border-divider rounded focus:outline-none focus:border-accent-primary"
                >
                  {SOCIAL_NETWORKS.map((network) => (
                    <option key={network.id} value={network.id}>{network.name}</option>
                  ))}
                </select>
              </div>

              {/* User Prompt */}
              <div>
                <label className="block text-sm font-medium mb-2">User Prompt</label>
                <textarea
                  value={settings.userPrompt}
                  onChange={(e) => setSettings(prev => ({ ...prev, userPrompt: e.target.value }))}
                  className="w-full h-32 px-3 py-2 bg-dark-200 border border-divider rounded focus:outline-none focus:border-accent-primary resize-none"
                  placeholder="Enter user prompt..."
                />
              </div>

              
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-dark-400 p-4">
          <button
            type="button"
            onClick={handleDelete}
            className={`flex items-center gap-1 text-sm transition-colors ${
              deleteConfirm 
                ? 'bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded' 
                : 'text-gray-400 hover:text-red-500'
            }`}
          >
            <XMarkIcon className="w-3.5 h-3.5" />
            <span>{deleteConfirm ? 'Click to confirm delete' : 'Delete node'}</span>
          </button>
        </div>
      </div>
    </Modal>
  );
} 